<template>
  <div class="space-y-6">
    <!-- Add/Edit Form -->
    <div v-if="showAddForm || editingBranch" class="space-y-4">
      <h4 class="text-base font-semibold text-hui-foreground border-b border-hui-divider pb-2">
        {{ editingBranch ? 'Редактировать филиал' : 'Новый филиал' }}
      </h4>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UiKitInput v-model="branchForm.name" label="Название филиала" placeholder="Введите название филиала" />
        <UiKitInput v-model="branchForm.address.city" label="Город" placeholder="Введите город" />
        <UiKitInput v-model="branchForm.address.street" label="Улица" placeholder="Введите улицу и дом" />
        <UiKitInput v-model="branchForm.phone" label="Телефон" placeholder="Введите телефон" />
        <UiKitInput
          v-model="branchForm.working_hours.hours"
          label="Часы работы"
          placeholder="Например: круглосуточно или 09:00-22:00"
          class="md:col-span-2"
        />
      </div>

      <!-- Столы -->
      <div class="space-y-3">
        <div class="flex items-center justify-between">
          <h5 class="text-sm font-semibold text-hui-foreground">Столы</h5>
          <UiKitButton size="sm" color="primary" variant="flat" @click="addTable">
            <template #leading><Icon name="heroicons:plus" class="w-4 h-4" /></template>
            Добавить стол
          </UiKitButton>
        </div>

        <div class="space-y-2">
          <div
            v-for="(table, index) in branchForm.tables"
            :key="index"
            class="grid grid-cols-[1fr_1fr_auto] gap-3 items-center"
          >
            <UiKitInput
              label="Номер стола"
              type="number"
              :model-value="String(table.number)"
              @update:model-value="(value: string) => table.number = Number(value)"
            />
            <UiKitInput
              label="Количество мест"
              type="number"
              :model-value="String(table.seats)"
              @update:model-value="(value: string) => table.seats = Number(value)"
            />
            <UiKitButton color="danger" variant="light" size="sm" @click="removeTable(index)">
              <Icon name="heroicons:trash" class="w-4 h-4" />
            </UiKitButton>
          </div>
        </div>
      </div>

      <div class="flex gap-3 pt-2">
        <UiKitButton color="primary" :loading="loading" :disabled="loading" @click="saveBranch">
          {{ editingBranch ? 'Обновить' : 'Создать' }}
        </UiKitButton>
        <UiKitButton color="default" variant="flat" @click="cancelEdit">Отмена</UiKitButton>
      </div>
    </div>

    <!-- Branch list -->
    <template v-else>
      <div v-if="loading" class="text-sm text-hui-foreground/60">Загрузка…</div>

      <UiKitAlert v-if="errorMessage" color="danger" class="mb-4">{{ errorMessage }}</UiKitAlert>

      <div class="flex justify-end">
        <UiKitButton color="primary" variant="flat" @click="showAddForm = true">
          <template #leading><Icon name="heroicons:plus" class="w-4 h-4" /></template>
          Добавить филиал
        </UiKitButton>
      </div>

      <div v-if="branches.length === 0 && !loading" class="text-sm text-hui-foreground/60">
        Филиалов нет
      </div>

      <div class="space-y-3">
        <div
          v-for="branch in branches"
          :key="branch.id"
          class="flex justify-between items-center p-4 bg-hui-default-100 rounded-xl border border-hui-divider"
        >
          <div class="flex-1 space-y-0.5">
            <p class="font-semibold text-hui-foreground">{{ branch.name || 'Без названия' }}</p>
            <p class="text-sm text-hui-foreground/60">
              <span v-if="branch.address">
                {{ branch.address.city }}{{ branch.address.city && branch.address.street ? ', ' : '' }}{{ branch.address.street || '' }}
              </span>
              <span v-else>Адрес не указан</span>
            </p>
            <p class="text-sm text-hui-foreground/60">Телефон: {{ branch.phone || 'Не указан' }}</p>
            <p class="text-sm text-hui-foreground/60">Часы: {{ branch.working_hours?.hours || 'Не указано' }}</p>
            <p class="text-sm text-hui-foreground/60">Столов: {{ branch.tables?.length || 0 }}</p>
            <UiKitChip :color="branch.is_active ? 'success' : 'danger'" size="sm" class="mt-1">
              {{ branch.is_active ? 'Активен' : 'Неактивен' }}
            </UiKitChip>
          </div>

          <div class="flex gap-2 ml-4">
            <UiKitButton color="primary" variant="flat" size="sm" @click="editBranch(branch)">
              <Icon name="heroicons:pencil" class="w-4 h-4" />
            </UiKitButton>
            <UiKitButton
              :color="branch.is_active ? 'warning' : 'success'"
              variant="flat"
              size="sm"
              @click="toggleBranchStatus(branch)"
            >
              <Icon :name="branch.is_active ? 'heroicons:eye-slash' : 'heroicons:eye'" class="w-4 h-4" />
            </UiKitButton>
            <UiKitButton color="danger" variant="flat" size="sm" @click="deleteBranch(branch.id!)">
              <Icon name="heroicons:trash" class="w-4 h-4" />
            </UiKitButton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import consola from 'consola'
import { useKitToast } from '@shared/ui/kit'
import { APIAdminInstitution } from '~/fsd-pages/admin/api'
import type { Branch, BranchTable } from '@entities/institution'

interface BranchForm {
  name: string
  address: {
    city: string
    street: string
  }
  phone: string
  working_hours: {
    hours: string
  }
  legal_information?: unknown
  social_networks?: unknown
  tables: BranchTable[]
}

const emit = defineEmits<{
  close: []
  'update:size': [size: string]
}>()

const { toast: kitToast } = useKitToast()
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    kitToast({ color: o.color === 'green' ? 'success' : o.color === 'red' ? 'danger' : o.color === 'orange' ? 'warning' : 'default', title: o.title, description: o.description, timeout: o.timeout }),
}

const loading = ref(false)
const branches = ref<Branch[]>([])
const showAddForm = ref(false)
const editingBranch = ref<Branch | null>(null)
const errorMessage = ref('')

const branchForm = ref<BranchForm>({
  name: '',
  address: { city: '', street: '' },
  phone: '',
  working_hours: { hours: '' },
  legal_information: null,
  social_networks: {},
  tables: [],
})

onMounted(() => {
  emit('update:size', 'l')
  loadBranches()
})

const loadBranches = async () => {
  try {
    loading.value = true
    const response = await APIAdminInstitution.getBranchesSettings()
    branches.value = response
  } catch (error) {
    consola.error('Ошибка загрузки филиалов:', error)
    errorMessage.value = 'Ошибка загрузки филиалов'
    toast.add({ title: 'Ошибка', description: 'Не удалось загрузить филиалы', color: 'red' })
  } finally {
    loading.value = false
  }
}

const addTable = () => {
  branchForm.value.tables.push({ number: 0, seats: 0 })
}

const removeTable = (index: number) => {
  branchForm.value.tables.splice(index, 1)
}

const saveBranch = async () => {
  try {
    loading.value = true
    const formData = { ...branchForm.value }

    if (editingBranch.value) {
      await APIAdminInstitution.updateBranch(editingBranch.value.id!, formData)
    } else {
      await APIAdminInstitution.createBranch(formData)
    }

    await loadBranches()
    cancelEdit()
    toast.add({ title: 'Сохранено', description: 'Данные филиала обновлены', color: 'green' })
  } catch (error) {
    consola.error('Ошибка сохранения филиала:', error)
    toast.add({ title: 'Ошибка', description: 'Не удалось сохранить филиал', color: 'red' })
  } finally {
    loading.value = false
  }
}

const editBranch = (branch: Branch) => {
  editingBranch.value = branch
  branchForm.value = {
    name: branch.name,
    address: {
      city: branch.address.city || '',
      street: branch.address.street || '',
    },
    phone: branch.phone,
    working_hours: { hours: branch.working_hours.hours || '' },
    legal_information: branch.legal_information,
    social_networks: branch.social_networks,
    tables: branch.tables || [],
  }
}

const cancelEdit = () => {
  showAddForm.value = false
  editingBranch.value = null
  branchForm.value = {
    name: '',
    address: { city: '', street: '' },
    phone: '',
    working_hours: { hours: '' },
    legal_information: null,
    social_networks: {},
    tables: [],
  }
}

const toggleBranchStatus = async (branch: Branch) => {
  try {
    if (branch.is_active) {
      await APIAdminInstitution.disableBranch(branch.id!)
    } else {
      await APIAdminInstitution.enableBranch(branch.id!)
    }
    await loadBranches()
  } catch (error) {
    consola.error('Ошибка изменения статуса филиала:', error)
    toast.add({ title: 'Ошибка', description: 'Не удалось изменить статус', color: 'red' })
  }
}

const deleteBranch = async (branchId: string | number) => {
  if (!confirm('Вы уверены, что хотите удалить этот филиал?')) return

  try {
    await APIAdminInstitution.deleteBranch(branchId)
    await loadBranches()
    toast.add({ title: 'Удалено', description: 'Филиал удалён', color: 'green' })
  } catch (error) {
    consola.error('Ошибка удаления филиала:', error)
    toast.add({ title: 'Ошибка', description: 'Не удалось удалить филиал', color: 'red' })
  }
}

</script>
